import React from "react";
import Drawer from "@material-ui/core/Drawer";
import CloseIcon from "@material-ui/icons/Close";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import { FB } from "../../assets/feedbackTheme";
import { Stage, QUESTION_VIDEO_STATUS_LABEL } from "./tracking";
import { StageIcon, OverallBadge, ProgressBar } from "./cells";

// Kazanım detayı: both pipelines for one AchievementId plus the raw question-video
// counts. Reads the already-loaded row (no extra request).

const styles = {
  paper: { width: "min(760px, 94vw)", backgroundColor: FB.surfaceAlt },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    padding: "22px 24px 18px",
    backgroundColor: FB.surface,
    borderBottom: `1px solid ${FB.border}`,
  },
  eyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: 1, color: FB.accent },
  title: { margin: "6px 0 0", fontSize: 18, fontWeight: 600, lineHeight: 1.35, color: FB.text },
  meta: { marginTop: 6, fontSize: 12, color: FB.textSecondary },
  closeBtn: {
    width: 34,
    height: 34,
    flex: "0 0 auto",
    display: "inline-grid",
    placeItems: "center",
    padding: 0,
    border: `1px solid ${FB.border}`,
    borderRadius: 8,
    backgroundColor: FB.surface,
    color: FB.textSecondary,
    cursor: "pointer",
  },
  body: { padding: "18px 24px 32px" },
  overview: { display: "grid", gridTemplateColumns: "190px 1fr", gap: 12 },
  card: {
    backgroundColor: FB.surface,
    border: `1px solid ${FB.border}`,
    borderRadius: 10,
    padding: 16,
  },
  cardLabel: {
    display: "block",
    marginBottom: 10,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: FB.textMuted,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    fontSize: 14,
    fontWeight: 600,
    color: FB.text,
  },
  sectionIcon: {
    width: 32,
    height: 32,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    backgroundColor: FB.accentSoft,
    color: FB.accent,
  },
  pipeline: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) 32px minmax(0,1fr) 32px minmax(0,1fr)",
    alignItems: "center",
  },
  step: { display: "flex", alignItems: "center", gap: 9, minWidth: 0 },
  stepLabel: { fontSize: 12, fontWeight: 600, color: FB.text, lineHeight: 1.3 },
  stepDetail: { fontSize: 11, color: FB.textSecondary, whiteSpace: "nowrap" },
  line: (done) => ({
    height: 2,
    margin: "0 6px",
    borderRadius: 999,
    backgroundColor: done ? FB.accentMid : FB.border,
  }),
  countsHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  countsGrid: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 },
  metric: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 11,
    borderRadius: 8,
    backgroundColor: FB.surfaceAlt,
    border: `1px solid ${FB.border}`,
  },
};

const Pipeline = ({ title, Icon, steps }) => (
  <section style={{ ...styles.card, marginTop: 12 }}>
    <div style={styles.sectionTitle}>
      <span style={styles.sectionIcon}>
        <Icon style={{ fontSize: 18 }} />
      </span>
      {title}
    </div>
    <div style={styles.pipeline}>
      {steps.map((step, i) => (
        <React.Fragment key={step.label}>
          <div style={styles.step}>
            <StageIcon stage={step.stage} label={step.label} size={34} round />
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={styles.stepLabel}>{step.label}</span>
              <span style={styles.stepDetail}>{step.detail}</span>
            </div>
          </div>
          {i < steps.length - 1 && <div style={styles.line(step.stage === Stage.Done)} />}
        </React.Fragment>
      ))}
    </div>
  </section>
);

const Metric = ({ label, value }) => (
  <div style={styles.metric}>
    <span style={{ fontSize: 11, color: FB.textSecondary }}>{label}</span>
    <strong style={{ fontSize: 18, color: FB.text, fontVariantNumeric: "tabular-nums" }}>
      {Number(value || 0)}
    </strong>
  </div>
);

const DetailDrawer = ({ row, onClose }) => {
  const d = row && row._d;
  const q = (row && row.question) || {};
  const v = (row && row.video) || {};

  const videoSteps = d
    ? [
        { label: "Ders videosu yüklendi", detail: `${Number(v.VideoCount || 0)} video`, stage: d.stages.lessonVideo },
        {
          label: "Etkileşim onaylandı",
          detail: `${Number(v.approvedInteractionVideoCount || 0)}/${Math.max(Number(v.VideoCount || 0), 1)} video`,
          stage: d.stages.interaction,
        },
        { label: "Sisteme girmeye hazır", detail: `Status ≥ 3: ${d.systemReady ? "Evet" : "Hayır"}`, stage: d.stages.systemReady },
      ]
    : [];

  const questionSteps = d
    ? [
        { label: "5 soru girildi", detail: `${Number(q.QuestionCount || 0)}/5 soru`, stage: d.stages.fiveQuestions },
        { label: "Soru videoları çekildi", detail: `${d.recordedQuestionVideos}/5 video`, stage: d.stages.questionVideos },
        { label: "Videolar onaylandı", detail: `${d.approvedQuestionVideos}/5 video`, stage: d.stages.questionsApproved },
      ]
    : [];

  return (
    <Drawer anchor="right" open={!!row} onClose={onClose} PaperProps={{ style: styles.paper }}>
      {row && (
        <div role="dialog" aria-label="Kazanım detayı">
          <div style={styles.header}>
            <div>
              <div style={styles.eyebrow}>KAZANIM DETAYI</div>
              <h2 style={styles.title}>{row.AchievementName}</h2>
              <div style={styles.meta}>
                {row.CourseName} · {row.SegmentTopicName} · ID {row.AchievementId}
              </div>
            </div>
            <button type="button" style={styles.closeBtn} aria-label="Detayı kapat" onClick={onClose}>
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>

          <div style={styles.body}>
            <div style={styles.overview}>
              <div style={styles.card}>
                <span style={styles.cardLabel}>Genel Durum</span>
                <OverallBadge done={d.overallDone} />
              </div>
              <div style={styles.card}>
                <span style={styles.cardLabel}>Toplam ilerleme</span>
                <div style={{ maxWidth: 300 }}>
                  <ProgressBar value={d.progress} />
                </div>
              </div>
            </div>

            <Pipeline title="Ders Videosu Pipeline" Icon={VideoLibraryIcon} steps={videoSteps} />
            <Pipeline title="Soru Pipeline" Icon={HelpOutlineIcon} steps={questionSteps} />

            <section style={{ ...styles.card, marginTop: 12 }}>
              <div style={styles.countsHead}>
                <span style={{ fontSize: 14, fontWeight: 600, color: FB.text }}>Soru video durumları</span>
                <span style={{ fontSize: 11, color: FB.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Latest log
                </span>
              </div>
              <div style={styles.countsGrid}>
                {Object.keys(QUESTION_VIDEO_STATUS_LABEL).map((key) => (
                  <Metric key={key} label={QUESTION_VIDEO_STATUS_LABEL[key]} value={q[key]} />
                ))}
              </div>
            </section>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default DetailDrawer;
